import Controller, { OnAfterFetch, OnClick, OnMount, OnReadSuccess, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../../../../controllers/BaseController';
import { BloodManagementStartAliases as BloodAliases, FragmentNames } from '../../../../../codegen';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { BloodBarcode, BloodCard, BloodManagementDevice, BloodManagementType, ReservedItem, ReservedProduction, UnityMeasure } from '../blood-management-types';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import moment from 'moment';
import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, MapperAPI, PanelAPI, TextAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import MedicationService from '../../../../../controllers/adep/services/medication-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { ResponseBodyProps } from '../../enteral-nutrition/enteral-nutrition-type';
import { HttpService, HttpStatus, JWTClaims, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../../constants/endpoints';
import { ActionType, AdminType, InfusionHourStatus, InfusionItemStatus, OptionCheck } from '../../../../../constants/infusion-solutions';
import { AbortableReadEvent, ClickEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { UnitMeasures, UnitMeasuresDose } from '../../enteral-nutrition/enteral-nutrition-start/enteral-nutrition-start-type';
import { ApplicationRoute } from '../../../../../constants/routes';
import { applicationContext, CredentialsValidationError, CredentialsValidationSuccess, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { AdminModalsService } from '../../../../../controllers/adep/services/admin-modals-service';
import { Value } from '@philips-internal/oswald-ui-core';
import { BarcodeUtils } from '../../../../../utils/barcode-utils';
import { DateFilterUtils } from '../../../../../utils/date-filter-utils';
import BarcodeService from '../../../../../services/barcode-service';
import { DisplayMessages, Params } from '../../administration-schedule/administration-schedule-types';
import TimelineContext, { TimeLineEventMedication } from '../../../services/timeline-service';
import { BodyCheckInfo, FirstMedicationCheckInfoResponse } from '../../medication-viewer/medication-viewer-types';
import jwtDecode from 'jwt-decode';
import InfusionService, { CreateFirstSolutionCheckRequest } from '../../../services/infusion-service';
import { SolutionType } from '../../../adjust-infusion-speed/adjust-infusion-speed-types';

enum BagReadgingMethod {
  STANDARD_BARCODE_BAGS = 'P',
  BLOOD_NUMBER = 'S'
}

@Controller({ component: BloodAliases.BLOOD_START_LAYOUT, fragment: FragmentNames.BLOOD_MANAGEMENT_START })
export default class BloodManagementStartController extends BaseController {

  @Inject(BloodAliases.BLOOD_START_NAME_TEXT)
  private nameTextApi!: TextAPI;

  @Inject(BloodAliases.BLOOD_START_DATE_TIME_TEXT)
  private dateTimeTextApi!: TextAPI;

  @Inject(BloodAliases.BLOOD_START_NEW_DATE_INPUT)
  private newDateInputApi!: InputAPI;

  @Inject(BloodAliases.BLOOD_START_FORM)
  private formApi!: FormAPI;

  @Inject(BloodAliases.BLOOD_START_DEVICE_MAPPER)
  private deviceMapperApi!: MapperAPI;

  @Inject(BloodAliases.BLOOD_START_UNITY_FIELD)
  private unityFieldApi!: FieldAPI;

  @Inject(BloodAliases.BLOOD_START_INFUSION_FORM_FIELD)
  private infusionFormFieldApi!: FieldAPI;

  @Inject(BloodAliases.BLOOD_START_DEVICE_FIELD)
  private deviceFieldApi!: FieldAPI;

  @Inject(BloodAliases.BLOOD_START_VOLUME_STEP_FIELD)
  private volumeStepFieldApi!: FieldAPI;

  @Inject(BloodAliases.BLOOD_START_BARCODE_PANEL)
  private barcodePanelApi!: PanelAPI

  @Inject(BloodAliases.BLOOD_START_BARCODE_PANEL_BAG)
  private barcodePanelBagApi!: PanelAPI

  @Inject(BloodAliases.BLOOD_START_BUTTON)
  private startButtonApi!: ButtonAPI;

  @Inject(BloodAliases.BLOOD_START_CARD_DECK)
  private cardDeckApi!: DeckAPI;

  @Inject(BloodAliases.BLOOD_START_CARDS_ACCORDION_PANEL)
  private cardsAccordionPanelApi!: PanelAPI;

  @Inject(BloodAliases.BLOOD_START_BARCODE)
  private barcodeApi!: BarcodeAPI;

  @Inject(BloodAliases.BLOOD_START_BARCODE_BAG)
  private barcodeBagApi!: BarcodeAPI;

  @Inject(BloodAliases.NOTES_TEXT_AREA)
  private notesTextAreaApi!: TextAreaAPI;

  @OnMount(BloodAliases.BLOOD_START_LAYOUT)
  handleBloodStartLayouMount(): void {
    const bloodBagReadingMethod = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.BLOOD_BAG_READING_METHOD);
    if (bloodBagReadingMethod === BagReadgingMethod.BLOOD_NUMBER) {
      this.barcodePanelBagApi.setVisible(true);
    } else {
      this.barcodePanelApi.setVisible(true);
    }

    const { bloodManagement } = this.context.getBulkData() as { bloodManagement: BloodManagementType };
    this.context.set({ bloodManagement: { ...bloodManagement, bloodBagReadingMethod } });
  }

  @OnMount(BloodAliases.BLOOD_START_FORM)
  handleMountForm(): void {
    InformationBarUtils.collapsedBar = true;
    AdminByScheduleParams.readParam = { noCardsRead: true };
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
    this.context.set({
      hasDevice: displayDevices === YesNoType.NO,
      materialCode: bloodManagement.materialSequence,
    });
  }

  @OnMount(BloodAliases.BLOOD_START_NEW_DATE_INPUT)
  handleMountNewDateInput(): void {
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    const newDate = moment().utc().format(ISO_FULL_DATE_FORMAT);
    this.dateTimeTextApi.setValue(moment(bloodManagement.scheduleDate).utc().format(ISO_FULL_DATE_FORMAT));
    this.newDateInputApi.setValue(newDate);
    this.context.set({ currentTime: newDate, dateTime: newDate });
  }

  @OnMount(BloodAliases.BLOOD_START_NAME_FIELD)
  handleMountMaterialNameField({ target }: EventData<FieldAPI>): void {
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    target.setValue(bloodManagement.materialDescription);
    this.nameTextApi.setValue(bloodManagement.materialDescription);
  }

  @OnMount(BloodAliases.BLOOD_START_INFUSION_FORM_AUTOCOMPLETE)
  handleMountInfusionFormAutocomplete(): void {
    this.fetchInfusionForm().then((response) => {
      const currentRecord = this.formApi.getRecord();
      const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;

      this.formApi.setRecord({
        ...currentRecord,
        infusionType: {
          unitMeasure: response[0].unitMeasure,
          unitMeasureValue: response[0].unitMeasureValue
        },
        unitMeasureBlood: {
          unitMeasure: bloodManagement.unitMeasurementDescription,
          unitMeasureValue: bloodManagement.unitMeasurement,
        }
      });
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  @OnMount(BloodAliases.BLOOD_START_DEVICE_AUTOCOMPLETE)
  handleMountDeviceAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    if (this.shouldDisplayDevices()) {
      target.setEntry('description');
      target.setPrimaryKey('description');
    }
  }

  @OnMount(BloodAliases.BLOOD_START_DEVICE_MAPPER)
  handleMountDeviceMapper(): void {
    if (this.shouldDisplayDevices()) {
      this.deviceMapperApi.setMap({ value: 'description' });
    }
  }

  @OnMount([BloodAliases.BLOOD_START_INFUSION_FORM_FIELD, BloodAliases.BLOOD_START_DEVICE_FIELD])
  handleMountDeviceField({ target }: EventData<FieldAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    target.setVisible(itemStatus === InfusionItemStatus.NOT_STARTED);
  }

  @OnMount(BloodAliases.BLOOD_START_DEVICE_FETCH)
  handleMountDeviceFetch({ target }: EventData<FetchAPI>): void {
    if (this.shouldDisplayDevices()) {
      const { encounterId } = this.historyService.getCurrentRouteParams();
      const deviceStatus = FoundationUtils
        .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
      this.context.set({ deviceStatus, encounterId });
      target.setEndpoint(Endpoint.EMAR_FIND_ADULT_TPN_DEVICE);
    }
  }

  @OnMount(BloodAliases.BLOOD_START_VOLUME_STEP_FIELD)
  handleMountVolumeStepField({ target }: EventData<FieldAPI>): void {
    const { totalVolume } = this.context.get('bloodManagement') as BloodManagementType;
    target.setValue(totalVolume ?? '');
  }

  @OnMount(BloodAliases.BLOOD_START_INFUSION_SPEED_FIELD)
  handleMountInfusionSpeedField({ target }: EventData<FieldAPI>): void {
    const { infusionSpeed } = this.context.get('bloodManagement') as BloodManagementType;
    target.setValue(infusionSpeed ?? 0);
  }

  @OnMount(BloodAliases.BLOOD_START_BUTTON)
  async handleMountStartButton(): Promise<void> {
    this.setStartButtonValue();
    this.validateIfBarcodeAndAccordionCardsIsVisible();
    this.paramFiveNotAllowedValidateAccordingUser();
  }

  private async paramFiveNotAllowedValidateAccordingUser():  Promise<void> {
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    const status = bloodManagement.status == InfusionHourStatus.CHECKED_FIRST;
    if(this.confirmPerformingSecondUser() && status){
      const userName = foundationContext.getUser()?.username;
      await this.statusBloodManagementCheck(String(userName));
    }
  }

  @OnMount(BloodAliases.BLOOD_START_BARCODE)
  handleBarcodeMount(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
    this.resetCardDefault();

    const { prescriptionNumber } = this.context.get('bloodManagement') as BloodManagementType;

    this.barcodeApi.setEnabled(false);
    HttpService.get(Endpoint.EMAR_BLOOD_GET_BAGS, {
      prescriptionNumber: prescriptionNumber
    })
      .then((response) => {
        const bloodBags = response?.body as BloodBarcode[];
        this.context.set({ bloodBags: bloodBags });
        this.barcodeApi.setEnabled(true);
      })
  }

  @OnMount(BloodAliases.BLOOD_START_BARCODE_BAG)
  handleBarcodeBagMount(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
    this.resetCardBag();
  }

  @OnAfterFetch(BloodAliases.BLOOD_START_UNITY_FETCH)
  handleAfterFetchUnityFetch(event: FetchEvent): void {
    const unityMeasure = event.data as UnitMeasuresDose[];
    this.unityFieldApi.setVisible(!!unityMeasure.length);
  }

  @OnAfterFetch(BloodAliases.BLOOD_START_INFUSION_FORM_FETCH)
  handleAfterFetchInfusionFormFetch(event: FetchEvent): void {
    const infusionForm = event.data as UnitMeasures[];
    this.infusionFormFieldApi.setVisible(!!infusionForm.length);
  }

  @OnAfterFetch(BloodAliases.BLOOD_START_DEVICE_FETCH)
  handleAfterFetchDeviceFetch(event: FetchEvent): void {
    const deviceFetchApi = event.target as unknown as FetchAPI;
    const devices = event.data as BloodManagementDevice[];

    deviceFetchApi.setParameters({ loading: false }, { skip: true });
    deviceFetchApi.clearPersistedState();

    this.deviceFieldApi.setVisible(!!devices.length);
  }

  @OnClick(BloodAliases.BLOOD_START_BUTTON)
  async handleClickStartButton(): Promise<void> {
    const isDoubleCheck = this.context.getBulkData().isDoubleCheck;
    isDoubleCheck === YesNoType.YES ? this.handleProcessDoubleCheck() : this.startBloodManagement();
  }

  private async validateStatusItem(bodyCheckInfo: BodyCheckInfo): Promise<boolean> {
    const responseFirstMedication = await MedicationService.statusMedicationCheck(bodyCheckInfo, true);
    return !!responseFirstMedication?.responseDescription;
  }

  private async handleProcessDoubleCheck(): Promise<void> {
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    const isFirstCheck = await this.validateStatusItem(this.getBodyCheckInfo(''));
    const statusIsNotFirstCheck = bloodManagement.status !== InfusionHourStatus.CHECKED_FIRST;

    if (statusIsNotFirstCheck && isFirstCheck) {
      this.context.set({ isGoToTimeline: false });
      await this.createFirstCheckItem();
    }
    this.verifyParameteFive(isFirstCheck, statusIsNotFirstCheck);
  }

  private confirmPerformingSecondUser(): boolean {
    return FoundationUtils.findModuleParam(FunctionName.ADEP_9129,
      ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK) == Params.NOT_ALLOWED;
  }

  private verifyParameteFive(isFirstCheck: boolean, statusIsNotFirstCheck: boolean): void {
    if (this.confirmPerformingSecondUser()) {
      !statusIsNotFirstCheck && !isFirstCheck ? this.startBloodManagement() : this.redirectToTimeline();
    } else {
      this.credentialValidationUser();
    }
  }

  credentialValidationUser(): void {
    foundationContext.promptCredentialsValidation({
      onDismiss: () => {
        this.startButtonApi.setEnabled(true);
      },
      onError: (event: CredentialsValidationError) => this.validateCredentialsWithError(event),
      onSuccess: (event: CredentialsValidationSuccess) => this.validateCredentialsWithSuccessHandler(event, true),
      validateButtonExpression: MessageService.translate(DisplayMessages.VALIDATE_BUTTON_EXP),
    });
  }

  remainingTries = 3;

  validateCredentialsWithError(event: CredentialsValidationError): void {
    const { abort } = event;
    this.startButtonApi.setEnabled(true);
    MedicationService.showToast(DisplayMessages.ERROR_VALIDATE_LOGIN, ToastLevel.ERROR);
    if (this.remainingTries > 0) {
      this.remainingTries = this.remainingTries - 1;
    }
    if (this.remainingTries === 0) {
      abort();
    }
  }

  async validateSecondUser(token: string): Promise<string | undefined> {
    const userValidateCredentials = jwtDecode<JWTClaims>(token).sub;
    const userSecondCheckInvalid = await this.statusBloodManagementCheck(userValidateCredentials)
    return userSecondCheckInvalid?.responseDescription
  }

  private async validateCredentialsWithSuccessHandler(event: CredentialsValidationSuccess, doubleCheck: boolean): Promise<void> {
    if (doubleCheck) {
      await this.processSecondCheck(event?.token?.rawToken());
      return;
    }
    this.startBloodManagement();
  }

  private async processSecondCheck(token: string): Promise<void> {
    const userSecondCheckInvalid = await this.validateSecondUser(token);
    if (userSecondCheckInvalid) {
      MedicationService.showToast(userSecondCheckInvalid, ToastLevel.ERROR);
      this.credentialValidationUser();
      return;
    }
    this.startButtonApi.setEnabled(false);
    this.startBloodManagement();
  }

  private async startBloodManagement(): Promise<void> {
    const isValid = await this.formApi.validate();
    if (isValid) {
      this.submitBlood();
    }
  }

  private createFirstCheckItem(): void {
    const timelineEvent = TimelineContext.get();
    const timeLineEventBloodManagement = timelineEvent as TimeLineEventMedication;
    const actionType = 0;
    const request = {
      actionType,
      infusionType: SolutionType.BLOOD_MANAGEMENT,
      itemSequence: timeLineEventBloodManagement.itemSequence,
      prescriptionNumber: timeLineEventBloodManagement.prescriptionNumber,
      scheduleSequence: timeLineEventBloodManagement.internalProcedureSequence,
    }
    this.createFirstBloodManagementCheck(request as CreateFirstSolutionCheckRequest);
  }

  private async createFirstBloodManagementCheck(request: CreateFirstSolutionCheckRequest): Promise<boolean> {
    return await InfusionService.createFirstInfusionCheck(request)
      .then((status) => {
        if (status >= HttpStatus.OK && status < HttpStatus.MULTIPLE_CHOICES) {
          MedicationService.showToast(DisplayMessages.FIRST_MED_CHECK_CREATED, ToastLevel.SUCCESS);
          this.context.set({ isGoToTimeline: true });
          this.startButtonApi.setValue(MessageService.translate('txt.start-transfusion'));
          return true;
        }
        return false;
      }).catch(response => {
        const body = response.body;
        const hasErrors = !!body?.errors;
        if (hasErrors) {
          body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
        return Promise.resolve(false);
      });
  }

  @OnClick(BloodAliases.BLOOD_START_CARD)
  async handleClickCard(event: ClickEvent): Promise<void> {
    const scheduleSequence = event.value.scheduleSequence;
    const bloodBagReadingMethod = this.context.get('bloodBagReadingMethod');
    const isDefaultMethod = bloodBagReadingMethod && bloodBagReadingMethod === BagReadgingMethod.STANDARD_BARCODE_BAGS;
    BarcodeUtils.dialogModalConfirm(scheduleSequence, isDefaultMethod ? this.resetCardDefaultDisableStartButton : this.resetCardBagDisableStartButton);
  }

  private async statusBloodManagementCheck(secondUser: string | null): Promise<FirstMedicationCheckInfoResponse | null> {
    const body = this.getBodyCheckInfo(secondUser);
    return await this.emarGetInfoFirstBloodManegementCheck(body);
  }

  private getBodyCheckInfo(secondUser: string | null): BodyCheckInfo {
    const timelineEvent = TimelineContext.get();
    const timeLineEventBloodManagement = timelineEvent as TimeLineEventMedication;
    const option = secondUser ? OptionCheck.SAME_USER : OptionCheck.FIRST_CHECK;
    const userName = secondUser ? secondUser : foundationContext.getUser()?.username || '';

    const body = {
      itemCode: Number(timeLineEventBloodManagement.itemSequence),
      itemType: timeLineEventBloodManagement.itemType,
      option,
      prescriptionNumber: String(timelineEvent.prescriptionNumber),
      procedureCode: Number(timeLineEventBloodManagement.itemCode),
      scheduleSequence: Number(timeLineEventBloodManagement.id),
      userName,
    }
    return body
  }

  private emarGetInfoFirstBloodManegementCheck(body: Record<string, Value>): Promise<FirstMedicationCheckInfoResponse | null> {

    return HttpService.post(Endpoint.EMAR_GET_INFO_FIRST_MEDICATION_CHECK, {}, { body })
      .then(response => {
        const responseBody = response.body as FirstMedicationCheckInfoResponse;
        if (responseBody?.responseDescription && body.option === OptionCheck.SAME_USER) {
          MedicationService.showToast(responseBody?.responseDescription, ToastLevel.ERROR);
          const bloodBagReadingMethod = this.context.get('bloodManagement').bloodBagReadingMethod;
          bloodBagReadingMethod === BagReadgingMethod.BLOOD_NUMBER ? this.barcodeBagApi.setEnabled(false) : this.barcodeApi.setEnabled(false);
          this.startButtonApi.setEnabled(false);
        }
        Promise.resolve(responseBody);
        return responseBody;
      });
  }

  @OnUnmount(BloodAliases.BLOOD_START_LAYOUT)
  onUnMountBloodStartLayout(): void {
    const isGoToTimeline = this.context.get('isGoToTimeline');
    if ([undefined, true].includes(isGoToTimeline)) {
      this.redirectToTimeline();
    }
  }

  @OnReadSuccess(BloodAliases.BLOOD_START_BARCODE)
  handleReadSuccessBarcode(event: AbortableReadEvent<BarcodeData>): void {
    const bloodBarcode = this.getBloodBarcode() || {};
    const barcodeRead = event.item.value.trim();

    if (bloodBarcode?.bag && barcodeRead) {
      this.proccessComponentBarcode(barcodeRead, bloodBarcode.bag);
    } else {
      this.proccessBagBarcode(barcodeRead);
    }
  }

  @OnReadSuccess(BloodAliases.BLOOD_START_BARCODE_BAG)
  handleBagReadSuccessBarcode(event: AbortableReadEvent<BarcodeData>): void {
    const bagRead = event.item.value.trim();

    const reservedProductions = this.context.get('reservedProductions') as ReservedProduction[];
    const reservedProductionRead = reservedProductions.find(r => r.bagNumber === bagRead);
    if (reservedProductionRead) {
      if (this.isExpirationDateValid(reservedProductionRead)) {
        MedicationService.showToast(MessageService.translate('txt.bag-read-with-success'), ToastLevel.SUCCESS);
        this.setProductionsInCardBag(true, bagRead);
        this.startButtonApi.setEnabled(true);
        this.barcodeBagApi.setEnabled(false);
        return;
      }
      MedicationService.showToast(MessageService.translate('txt.blood-bag-expired'), ToastLevel.ERROR);
      return;
    }

    MedicationService.showToast(MessageService.translate('txt.invalid-bag-code'), ToastLevel.ERROR);
  }

  private isExpirationDateValid(reservedProduction: ReservedProduction): boolean {
    return DateFilterUtils.dateIsAfterNow(reservedProduction.endDate);
  }

  private proccessComponentBarcode(barcodeRead: string, bagRead: string): void {
    const bloodBagAndComponentRead = this.getBloodBagAndComponent(bagRead, barcodeRead);
    if (bloodBagAndComponentRead) {
      this.setBeepedComponent(barcodeRead);
      this.handleBloodBarcodeResponse(bloodBagAndComponentRead.prescriptionNumber);
      return
    }

    MedicationService.showToast(MessageService.translate('txt.blood-component-code-invalid'), ToastLevel.ERROR);
    return;
  }

  private handleBloodBarcodeResponse(prescriptionFromCodeRead: number): void {
    this.setItemInCard(true, true, prescriptionFromCodeRead);

    MedicationService.showToast(MessageService.translate('txt.blood-component-read-with-success'), ToastLevel.SUCCESS);
    this.startButtonApi.setEnabled(true);
    this.barcodeApi.setEnabled(false);
    this.context.set({ allItemsRead: true });
  }

  private proccessBagBarcode(barcodeRead: string): void {
    if (!this.checkBloodBagValid(barcodeRead)) {
      MedicationService.showToast(MessageService.translate('txt.invalid-bag-code'), ToastLevel.ERROR);
      return;
    }

    MedicationService.showToast(MessageService.translate('txt.bag-read-with-success'), ToastLevel.SUCCESS);
    this.barcodeApi.setManualInputBarcodeExpression('txt.read-blood-component');
    this.setItemInCard(true, false, 0);
    this.setBeepedBag(barcodeRead);
  }

  private setBeepedBag(bag: string): void {
    const bloodBarcode = { bag: bag };
    this.context.set({ bloodBarcode });
  }

  private getBeepedBag(): string {
    const bloodBarcode = this.getBloodBarcode();
    return bloodBarcode?.bag || '';
  }

  private setBeepedComponent(component: string): void {
    const bloodBarcode = this.getBloodBarcode();
    bloodBarcode.component = component;
    this.context.set({ bloodBarcode });
  }

  private getBeepedComponent(): string {
    const bloodBarcode = this.getBloodBarcode();
    return bloodBarcode?.component || '';
  }

  private getBloodBarcode(): BloodBarcode {
    return this.context.get('bloodBarcode') as BloodBarcode;
  }

  private resetBarcode(): void {
    this.barcodeApi.setManualInputBarcodeExpression('txt.read-bag');
    this.context.set({ bloodBarcode: {} });
  }

  private validateIfBarcodeAndAccordionCardsIsVisible(): void {
    const isRestartSolution = this.context.get('actionType') === ActionType.RESTART_SOLUTION;
    const { stepNumber } = this.context.get('bloodManagement') as BloodManagementType;

    if (this.checkDisableBarcode(isRestartSolution, stepNumber)) {
      this.barcodePanelApi.setVisible(false);
      this.barcodePanelBagApi.setVisible(false);
      this.cardsAccordionPanelApi.setVisible(false);
      this.startButtonApi.setEnabled(true);
      return;
    }
  }

  private checkDisableBarcode(isRestartSolution: boolean, stepNumber: number): boolean {
    return (isRestartSolution || stepNumber > 1);
  }

  resetCardDefault = (): void => {
    this.resetBarcode();
    this.setItemInCard(false, false, 0);
    this.barcodeApi.setEnabled(true);
  }

  resetCardBag = (): void => {
    this.setProductionsInCardBag(false, '');
    this.barcodeBagApi.setEnabled(true);
  }

  resetCardDefaultDisableStartButton = (): void => {
    this.resetCardDefault();
    this.startButtonApi.setEnabled(false);
  }

  resetCardBagDisableStartButton = (): void => {
    this.resetCardBag();
    this.startButtonApi.setEnabled(false);
  }

  private setProductionsInCardBag(bagRead: boolean, bagNumber: string): void {
    this.loadReservedProductions().then((reservedProductions) => {
      const textMilliliter = MessageService.translate('tws.measure-label');

      const bloodbagsCard: BloodCard[] = reservedProductions.map(reservedProduction => {
        const isItemRead = bagRead && (reservedProduction.bagNumber === bagNumber);
        isItemRead && this.volumeStepFieldApi.setValue(reservedProduction.volume);

        return {
          barcode: '',
          endDate: reservedProduction.endDate,
          materialDescription: reservedProduction.description,
          read: isItemRead,
          scheduleSequence: reservedProduction.id,
          status: reservedProduction.status,
          unitMeasureCode: textMilliliter,
          volume: reservedProduction.volume
        };
      });

      this.cardDeckApi.setData([
        ...bloodbagsCard
      ]);
    });
  }

  private setItemInCard(bagRead: boolean, componentRead: boolean, codeComponentRead: number): void {
    const textMilliliter = MessageService.translate('tws.measure-label');
    const textBloodBag = MessageService.translate('txt.blood-bag');

    this.loadReservedItems().then((reservedItems) => {

      const bloodBagCard = {
        barcode: '',
        doseQuantity: 1,
        materialDescription: textBloodBag,
        read: bagRead,
        scheduleSequence: 'bag',
        unitMeasureCode: 'Bs'
      };

      const bloodComponentsCard: BloodCard[] = reservedItems.map(reservedItem => {
        const isItemRead = componentRead && (codeComponentRead === reservedItem.prescriptionNumber);
        isItemRead && this.volumeStepFieldApi.setValue(reservedItem.volume);

        return {
          barcode: '',
          doseQuantity: reservedItem.amount,
          materialDescription: reservedItem.description,
          read: isItemRead,
          scheduleSequence: reservedItem.id,
          status: reservedItem.status,
          unitMeasureCode: textMilliliter,
          volume: reservedItem.volume ?? 0
        };
      });

      this.cardDeckApi.setData([
        bloodBagCard,
        ...bloodComponentsCard
      ]);
    })
  }

  private getBloodBags(): BloodBarcode[] {
    return this.context.get('bloodBags') as BloodBarcode[];
  }

  private checkBloodBagValid(bag: string): boolean {
    return this.getBloodBags()?.some(b => b.bag === bag);
  }

  private getBloodBagAndComponent(bag: string, component: string): BloodBarcode | undefined {
    return this.getBloodBags()?.find(b => b.bag === bag && b.component === component);
  }

  private submitBlood(): void {
    const requestBody = this.getBloodRequestBody();

    this.startButtonApi.setEnabled(false);

    HttpService.post(Endpoint.EMAR_START_BLOOD, { bag: this.bagToStartBlood() }, { body: requestBody })
      .then((response) => {
        this.showResponseMessage(response.body as string);
        this.redirectToTimeline();
      })
      .catch((error) => this.handleApiError(error));
  }

  private bagToStartBlood(): string {
    const beepedBag = this.getBeepedBag();
    const beepedComponent = this.getBeepedComponent();

    if (beepedBag && beepedComponent) {
      return `${beepedBag}/${beepedComponent}`;
    }

    return '';
  }

  private getBloodRequestBody(): Record<string, Value> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    const formRecord = this.formApi.getRecord();
    const currentTime = moment(String(this.newDateInputApi.getValue())).format(ISO_FULL_DATE_FORMAT);
    const actionType = this.context.get('actionType');
    const infusionTypeForm = formRecord.infusionType as { unitMeasure?: string, unitMeasureValue?: string };
    const infusionType = this.getInfusionType(infusionTypeForm.unitMeasureValue)
    const device = formRecord.device as BloodManagementDevice;
    const unityMeasure = formRecord.unitMeasureBlood as UnityMeasure;

    const body = {
      actionType,
      changeDate: currentTime,
      dateTime: bloodManagement.scheduleDate,
      deviceSequence: Number(device?.deviceCode),
      dosageType: unityMeasure?.unitMeasureCode,
      encounterNumber: encounterId,
      infusionSpeedQuantity: formRecord?.infusionSpeed,
      infusionType,
      justifyDescription: this.notesTextAreaApi.getValue() as string,
      observationDescription: formRecord.observation ?? '',
      prescriptionNumber: Number(bloodManagement.prescriptionNumber),
      solutionSequence: bloodManagement.materialSequence,
      volumePhaseQuantity: Number(String(formRecord.stepVolume).split(' ')[0])
    } as Record<string, Value>;

    return body;
  }

  private getInfusionType(unitMeasureValue?: string): AdminType {
    return unitMeasureValue !== AdminType.OPEN && unitMeasureValue !== AdminType.TIME &&
      unitMeasureValue !== AdminType.INFUSION_SPEED ? AdminType.INFUSION_SPEED : unitMeasureValue;
  }

  private setStartButtonValue(): void {
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));

    if (bloodManagement.status === InfusionHourStatus.INTERRUPTED) {
      this.context.set({ actionType: ActionType.RESTART_SOLUTION });
      this.startButtonApi.setValue(MessageService.translate('txt.restart-transfusion'));
      return;
    }

    if (bloodManagement.status !== InfusionHourStatus.STARTED) {
      this.context.set({ actionType: ActionType.START_SOLUTION });
      const isDoubleCheck = this.context.getBulkData().isDoubleCheck;
      const labelButton = isDoubleCheck == YesNoType.YES && bloodManagement.status != InfusionHourStatus.CHECKED_FIRST ?
        MessageService.translate('tws.emar.perform-first-check') :
        MessageService.translate('txt.start-transfusion');
      this.startButtonApi.setValue(labelButton);
      return;
    }
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    const actionType = this.context.get('actionType');
    switch (actionType) {
      case ActionType.START_SOLUTION:
        MedicationService.showToast(MessageService.translate('txt.item-started-with-success'), ToastLevel.SUCCESS);
        break;
      case ActionType.RESTART_SOLUTION:
        MedicationService.showToast(MessageService.translate('txt.item-restarted-with-success'), ToastLevel.SUCCESS);
        break;
      default:
        break;
    }
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.BLOOD_MANAGEMENT_START);
    AdminModalsService.redirectToTimeline(FragmentNames.BLOOD_MANAGEMENT_VIEWER);
  }

  private fetchInfusionForm(): Promise<ResponseBodyProps[]> {
    const infusionFormDomainSequence = 1883;
    const activeSituation = 'A';

    return HttpService.get(Endpoint.EMAR_FIND_INFUSIONS_FORM, { domain: infusionFormDomainSequence, situation: activeSituation })
      .then(response => response.body as ResponseBodyProps[]);
  }

  private async fetchReservedItems(): Promise<ReservedItem[]> {
    const { prescriptionNumber } = this.context.get('bloodManagement') as BloodManagementType;

    const response = await HttpService.get(Endpoint.EMAR_BLOOD_GET_RESERVED_ITEMS, { prescriptionNumber });
    const reservedItems = response.body as ReservedItem[];
    this.context.set({ reservedItems: reservedItems });
    return reservedItems;
  }

  private async fetchReservedProductions(): Promise<ReservedProduction[]> {
    const bloodManagement = this.context.get('bloodManagement') as BloodManagementType;
    const prescriptionNumber = bloodManagement.prescriptionNumber;

    const response = await HttpService.get(Endpoint.EMAR_BLOOD_GET_RESERVED_PRODUCTIONS, { prescriptionNumber });
    const reservedProductions = response.body as ReservedProduction[];
    this.context.set({ reservedProductions: reservedProductions });
    return reservedProductions;
  }

  private loadReservedProductions(): Promise<ReservedProduction[]> {
    const reservedIProductions = this.context.get('reservedProductions');
    return Promise.resolve(reservedIProductions ? reservedIProductions : this.fetchReservedProductions());
  }

  private loadReservedItems(): Promise<ReservedItem[]> {
    const reservedItems = this.context.get('reservedItems');
    return Promise.resolve(reservedItems ? reservedItems : this.fetchReservedItems());
  }

  private shouldDisplayDevices(): boolean {
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
    return displayDevices === YesNoType.YES;
  }

  private handleApiError(error: any): void {
    if (error.body && error.body.errors) {
      error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
    }
  }
}
